<template>
  <section class="elite-experience-section">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-lg-6">
          <div class="elite-content">
            <h2 class="elite-title">{{ headerData.title }}</h2>
            <p class="elite-description">{{ headerData.description }}</p>
            <div class="elite-features">
              <div class="elite-feature" v-for="(feature, index) in eliteFeatures" :key="index">
                <div class="feature-luxury-icon">
                  <i :class="feature.icon"></i>
                </div>
                <div class="feature-luxury-text">
                  <h4>{{ feature.title }}</h4>
                  <p>{{ feature.description }}</p>
                </div>
              </div>
            </div>
            <button class="elite-btn">{{ buttonText }}</button>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="elite-visual">
            <div class="luxury-showcase">
              <div class="showcase-item" v-for="(item, index) in luxuryShowcase" :key="index">
                <img :src="item.image" :alt="item.title" />
                <div class="showcase-overlay">
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ServiceItsExperienceComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        title: "The Elite Experience",
        description: "Step into a world of unparalleled luxury and sophistication. Our elite service experience is designed to exceed the expectations of the most discerning automotive enthusiasts."
      })
    },
    eliteFeatures: {
      type: Array,
      required: true,
      default: () => []
    },
    luxuryShowcase: {
      type: Array,
      required: true,
      default: () => []
    },
    buttonText: {
      type: String,
      required: false,
      default: "Experience Elite"
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Elite Experience Section */
.elite-experience-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.elite-content {
  padding-right: 50px;
}

.elite-title {
  font-size: 48px;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 25px;
  font-family: 'Archivo', sans-serif;
  line-height: 1.2;
}

.elite-description {
  font-size: 20px;
  color: #6c757d;
  margin-bottom: 50px;
  line-height: 1.7;
}

.elite-features {
  margin-bottom: 50px;
}

.elite-feature {
  display: flex;
  align-items: flex-start;
  gap: 25px;
  margin-bottom: 35px;
  padding: 25px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.elite-feature:hover {
  transform: translateX(10px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
}

.feature-luxury-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #d4af37, #b8860b);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.elite-feature:hover .feature-luxury-icon {
  transform: scale(1.1) rotate(5deg);
}

.feature-luxury-icon i {
  font-size: 28px;
  color: #1a1a1a;
}

.feature-luxury-text h4 {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
  font-family: 'Archivo', sans-serif;
}

.feature-luxury-text p {
  font-size: 16px;
  color: #6c757d;
  line-height: 1.6;
}

.elite-btn {
  background: linear-gradient(135deg, #d4af37, #b8860b);
  color: #1a1a1a;
  border: none;
  padding: 18px 40px;
  border-radius: 35px;
  font-weight: 700;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.4s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.3);
  position: relative;
  overflow: hidden;
}

.elite-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s;
}

.elite-btn:hover::before {
  left: 100%;
}

.elite-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(212, 175, 55, 0.4);
}

.elite-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px;
}

.luxury-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
  max-width: 500px;
}

.showcase-item {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  transition: all 0.4s ease;
  height: 200px;
}

.showcase-item:hover {
  transform: translateY(-10px) scale(1.05);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
}

.showcase-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.showcase-item:hover img {
  transform: scale(1.1);
}

.showcase-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.9), rgba(184, 134, 11, 0.9));
  color: #1a1a1a;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.4s ease;
  text-align: center;
}

.showcase-item:hover .showcase-overlay {
  opacity: 1;
}

.showcase-overlay h4 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  font-family: 'Archivo', sans-serif;
}

.showcase-overlay p {
  font-size: 14px;
  line-height: 1.4;
  color: rgba(26, 26, 26, 0.8);
}

/* Responsive Design */
@media (max-width: 991px) {
  .elite-content {
    padding-right: 0;
    margin-bottom: 50px;
  }
  
  .elite-visual {
    height: 400px;
  }
  
  .luxury-showcase {
    max-width: 400px;
  }
  
  .showcase-item {
    height: 180px;
  }
}

@media (max-width: 768px) {
  .elite-title {
    font-size: 36px;
  }
  
  .elite-description {
    font-size: 18px;
  }
  
  .elite-feature {
    padding: 20px;
    margin-bottom: 25px;
  }
  
  .feature-luxury-icon {
    width: 60px;
    height: 60px;
  }
  
  .feature-luxury-icon i {
    font-size: 24px;
  }
  
  .elite-visual {
    height: 350px;
  }
  
  .luxury-showcase {
    grid-template-columns: 1fr;
    gap: 15px;
    max-width: 300px;
  }
  
  .showcase-item {
    height: 150px;
  }
}
</style>
